// The app's contents page: a persistent left stepper on every view except the
// full-screen graph — 5 phase headers with progress counts, 8 step rows with
// live status dots, current step highlighted, descriptions as tooltips. The
// rail NAVIGATES; workspaces ACT — generate/mark-complete actions stay in the
// workspace headers. Don't add actions or summaries here: that rebuilds the
// retired journey page sideways and reintroduces the bouncing this shell
// exists to fix. Collapses to icons below md.

import { cn } from '@/lib/utils';
import { JOURNEY_PHASES } from '@/features/project/journey-steps';
import { useStepStatuses, type StepStatus } from '@/features/project/step-status';
import { useUIStore } from '@/stores';

const DOT_COLOR: Record<StepStatus, string> = {
  'not-started': 'bg-border',
  'in-progress': 'bg-amber-500',
  'done': 'bg-emerald-500',
};

// `landingPending`: the shell is still resolving which step to land on, so the
// store's viewMode is a placeholder. Suppress the active highlight until it's
// real, otherwise the rail briefly lights the placeholder step then jumps.
export function JourneyRail({
  projectId,
  landingPending = false,
}: {
  projectId: string;
  landingPending?: boolean;
}) {
  const viewMode = useUIStore((s) => s.viewMode);
  const setViewMode = useUIStore((s) => s.setViewMode);
  const statuses = useStepStatuses(projectId);

  return (
    <nav
      aria-label="Validation journey"
      className="w-12 md:w-52 shrink-0 border-r bg-card overflow-y-auto py-2 px-1.5 md:px-2 space-y-1"
    >
      {JOURNEY_PHASES.map((phase, i) => {
        const doneCount = phase.steps.filter((s) => statuses?.[s.id] === 'done').length;
        return (
          <div key={phase.name} className={cn(i > 0 && 'pt-1 border-t md:border-t-0')}>
            <div className="hidden md:flex items-baseline justify-between px-2 pt-2 pb-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {phase.name}
              </span>
              <span
                className={cn(
                  'text-[10px] tabular-nums',
                  doneCount === phase.steps.length ? 'text-emerald-600' : 'text-muted-foreground',
                )}
              >
                {doneCount}/{phase.steps.length}
              </span>
            </div>
            <div className="space-y-0.5">
              {phase.steps.map((step) => {
                const active = !landingPending && step.view === viewMode;
                const status = statuses?.[step.id] ?? 'not-started';
                const Icon = step.icon;
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setViewMode(step.view)}
                    title={`${step.step}. ${step.title} — ${step.description}`}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                      active
                        ? 'bg-accent text-foreground font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <span className="relative shrink-0 mx-auto md:mx-0">
                      <Icon className="h-4 w-4" />
                      <span
                        aria-hidden
                        className={cn(
                          'absolute -top-0.5 -right-1 h-2 w-2 rounded-full border border-card',
                          DOT_COLOR[status],
                        )}
                      />
                    </span>
                    <span className="hidden md:block truncate text-left flex-1">{step.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
  );
}
